import { AlertTriangle, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { deleteCosto } from "../../api";
import type { CostoFamilia, CostoHecho, CostosUnificado } from "../../types";
import { CostoForm, type CostoPrefill } from "./CostoForm";
import { CostosChart } from "./CostosChart";
import { estadoGcp, etiquetas, filasDelMes, modeloMes, mesesVisibles, prefillDe, type Filtro } from "./costosData";
import { FAMILIAS, fmtClp, fmtClpCorto, mesLargo } from "./costosFormat";

interface Props {
  data: CostosUnificado;
  theme: "light" | "dark";
  onReload: () => Promise<void>;
}

/** Costos en escritorio: todo el mes a la vista, con tabla de detalle y formulario en línea. */
export function CostosView({ data, theme, onReload }: Props) {
  const [selected, setSelected] = useState(data.actual);
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const [form, setForm] = useState<CostoPrefill | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const t = data.totales[selected];
  const m = modeloMes(data, selected);
  const filas = useMemo(() => filasDelMes(data, selected, filtro), [data, selected, filtro]);
  const maxDelta = Math.max(1, ...m.suben.map((s) => s.delta));

  const abrirForm = (p: CostoPrefill) => { setMensaje(null); setError(null); setForm(p); };
  const guardado = async (msg: string) => { setForm(null); setMensaje(msg); await onReload(); };
  const borrar = async (h: CostoHecho) => {
    if (!h.id || !window.confirm(`¿Eliminar "${h.servicio}" (${fmtClp(h.monto_clp)})${h.recurrente ? " de todos los meses" : ""}?`)) return;
    try { await deleteCosto(h.id); setMensaje("Costo eliminado"); await onReload(); }
    catch (e: any) { setError(e?.message || "No se pudo eliminar."); }
  };
  const editar = (h: CostoHecho) => abrirForm(prefillDe(h));

  const tarjetaFamilia = (f: CostoFamilia, valor: string, sub: string, critico = false) => (
    <button key={f} type="button" className={`sbx-card sbx-kpi cst-kpi-btn${critico ? " is-critical" : ""}`} onClick={() => setFiltro(f)} aria-pressed={filtro === f}>
      <span className="sbx-kpi-label">{FAMILIAS.find((x) => x.key === f)!.label}</span>
      <span className="sbx-kpi-value"><strong>{valor}</strong></span>
      <span className="sbx-kpi-sub">{sub}</span>
    </button>
  );

  return (
    <div className="sbx cst" data-theme={theme}>
      <header className="sbx-head">
        <div className="sbx-head-title">
          <span className="sbx-tag">Finanzas · Exclusivo Dirección</span>
          <h1>Costos</h1>
          <p>BigQuery y GCP, suscripciones e IA en un solo lugar. Montos en CLP.</p>
        </div>
        <div className="sbx-head-controls">
          <select className="cst-select" value={selected} onChange={(e) => setSelected(e.target.value)} aria-label="Mes">
            {[...data.meses].reverse().map((mes) => <option key={mes} value={mes}>{mesLargo(mes)}{mes === data.actual ? " (en curso)" : ""}</option>)}
          </select>
          <button type="button" className="cst-primary" onClick={() => abrirForm({})}><Plus size={16} />Cargar costo</button>
        </div>
      </header>

      {mensaje && <div className="cst-banner ok">{mensaje}</div>}
      {error && <div className="cst-banner bad">{error}</div>}
      {form && <section className="sbx-card"><CostoForm mes={selected} prefill={form} onSaved={guardado} onCancel={() => setForm(null)} /></section>}

      <section className="sbx-grid-kpi cst-kpis">
        <div className="sbx-card sbx-kpi">
          <span className="sbx-kpi-label">Total de {mesLargo(selected)}</span>
          <span className="sbx-kpi-value">
            <strong>{t?.total ? fmtClp(t.total) : t?.gcp_sin_datos ? "Sin datos" : fmtClp(0)}</strong>
            {m.deltaPct != null && <span className={`sbx-kpi-delta ${m.deltaPct > 0 ? "is-bad" : "is-good"}`}>{m.deltaPct > 0 ? "+" : ""}{m.deltaPct.toFixed(0)} % vs mes anterior</span>}
          </span>
          <span className="sbx-kpi-sub">{m.gcpIncompleto ? "Sin GCP completo: no se compara con el mes anterior" : "Suma de las tres familias"}</span>
        </div>
        {m.esActual ? (
          <div className="sbx-card sbx-kpi">
            <span className="sbx-kpi-label">Proyección a fin de mes</span>
            <span className="sbx-kpi-value"><strong>{fmtClp(data.forecast.total)}</strong></span>
            <span className="sbx-kpi-sub">
              {data.forecast.completo ? `Día ${data.forecast.dias_transcurridos} de ${data.forecast.dias_mes}` : `Sin ${data.forecast.gcp == null ? "GCP" : "IA"}: la proyección no incluye esa familia`}
            </span>
          </div>
        ) : (
          <div className="sbx-card sbx-kpi">
            <span className="sbx-kpi-label">IA ya incluida en GCP</span>
            <span className="sbx-kpi-value"><strong>{fmtClp(t?.atribucion_ia)}</strong></span>
            <span className="sbx-kpi-sub">Gemini y bots: se muestra pero no se suma</span>
          </div>
        )}
        {tarjetaFamilia("gcp", t?.gcp_sin_datos ? "Sin datos" : fmtClp(t?.gcp), estadoGcp(data, selected), !!t?.gcp_sin_datos)}
        {tarjetaFamilia("manual", t?.manual || m.hayManual ? fmtClp(t?.manual) : "Sin cargar", m.esActual && data.faltantes.length > 0 ? `${data.faltantes.length} suscripciones sin cargar` : "Cargado a mano")}
        {tarjetaFamilia("ia", fmtClp(t?.ia), "Cuentas cargadas y API directa (DeepSeek, Claude)")}
      </section>

      <section className="sbx-section">
        <div className="sbx-section-head"><span className="sbx-tag">Evolución</span><h2>Costo por mes</h2><p>Toca un mes para ver su detalle.</p></div>
        <div className="sbx-card"><CostosChart meses={mesesVisibles(data)}totales={data.totales} actual={data.actual} forecast={data.forecast} selected={selected} onSelect={setSelected} /></div>
      </section>

      <section className="sbx-section">
        <div className="sbx-section-head"><span className="sbx-tag">Qué hacer</span><h2>Recomendaciones de ahorro</h2><p>Salen de reglas sobre los datos de arriba, sin llamar a ninguna IA.</p></div>
        {data.recomendaciones.length === 0 ? (
          <div className="sbx-card sbx-note">Sin recomendaciones con los datos actuales.</div>
        ) : (
          <div className="sbx-grid-cards">
            {data.recomendaciones.map((r) => (
              <article key={r.id} className={`sbx-card cst-rec ${r.severidad}`}>
                <div className="cst-rec-top"><span className={`cst-chip ${r.severidad}`}>{r.severidad}</span>{r.ahorro_clp != null && <span className="cst-rec-ahorro">Monto asociado {fmtClpCorto(r.ahorro_clp)}</span>}</div>
                <strong>{r.titulo}</strong>
                <p>{r.detalle}</p>
                <p className="cst-rec-accion">{r.accion}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="sbx-section">
        <div className="sbx-section-head"><span className="sbx-tag">Tendencia</span><h2>Qué sube más</h2><p>{m.previo ? `${mesLargo(m.previo)} → ${mesLargo(selected)}, por monto.` : "Se necesita un mes anterior."}</p></div>
        <div className="sbx-card cst-suben">
          {m.suben.length === 0 && <p className="sbx-note">Ningún servicio sube respecto del mes anterior.</p>}
          {m.suben.map((s) => (
            <div key={`${s.familia}-${s.servicio}`} className="cst-sube">
              <div className="cst-sube-top"><span>{s.servicio}</span><strong>+{fmtClpCorto(s.delta)}{s.pct != null ? ` · ${s.pct.toFixed(0)} %` : " · nuevo"}</strong></div>
              <div className="cst-sube-bar"><i className={`cst-${s.familia}`} style={{ width: `${(s.delta / maxDelta) * 100}%` }} /></div>
            </div>
          ))}
          {m.gcpIncompleto && <p className="sbx-note"><AlertTriangle size={14} /> GCP no tiene el mes completo: sus alzas no son comparables.</p>}
        </div>
      </section>

      <section className="sbx-section">
        <div className="sbx-section-head"><span className="sbx-tag">Detalle</span><h2>{mesLargo(selected)}</h2></div>
        <div className="sbx-pills" role="group" aria-label="Familia">
          {([["todas", "Todas"], ...FAMILIAS.map((f) => [f.key, f.short])] as [Filtro, string][]).map(([k, l]) => (
            <button key={k} type="button" className="sbx-pill" aria-pressed={filtro === k} onClick={() => setFiltro(k)}>{l}</button>
          ))}
        </div>
        {filas.length === 0 ? (
          <div className="sbx-card sbx-note">Sin registros{filtro !== "todas" ? " en esta familia" : ""} para el mes.</div>
        ) : (
          <div className="sbx-card cst-table-wrap">
            <table className="cst-table">
              <thead><tr><th>Familia</th><th>Servicio</th><th>Origen</th><th className="num">Monto</th><th /></tr></thead>
              <tbody>
                {filas.map((h, i) => (
                  <tr key={`${h.servicio}-${i}`} className={h.en_total ? "" : "attr"}>
                    <td>{h.proveedor}</td>
                    <td>{h.servicio}</td>
                    <td>{etiquetas(h).map((e) => <span key={e} className="cst-chip">{e}</span>)}</td>
                    <td className="num">{fmtClp(h.monto_clp)}</td>
                    <td className="act">
                      {h.fuente === "manual" && h.id && (<>
                        <button type="button" className="cst-icon" onClick={() => editar(h)} aria-label="Editar"><Pencil size={15} /></button>
                        <button type="button" className="cst-icon" onClick={() => void borrar(h)} aria-label="Eliminar"><Trash2 size={15} /></button>
                      </>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="cst-lower">
        {m.esActual && data.faltantes.length > 0 && (
          <section className="sbx-section">
            <div className="sbx-section-head"><span className="sbx-tag">Sin fuente</span><h2>Falta cargar</h2><p>Gastos esperados que aún no figuran en {mesLargo(selected)}.</p></div>
            <div className="sbx-pills">
              {data.faltantes.map((f) => (
                <button key={f.nombre} type="button" className="sbx-pill" onClick={() => abrirForm({ categoria: f.categoria, concepto: f.nombre })}><Plus size={14} /> {f.nombre}</button>
              ))}
            </div>
          </section>
        )}
        {data.consultas_bq.length > 0 && (
          <section className="sbx-section">
            <div className="sbx-section-head"><span className="sbx-tag">BigQuery</span><h2>Consultas que más escanean</h2><p>Últimos 30 días, por TiB facturados.</p></div>
            <div className="cst-cards">
              {data.consultas_bq.map((c) => (
                <article key={c.firma} className="sbx-card cst-row-card">
                  <div className="cst-row-top"><strong>{c.pct.toFixed(0)} % · {c.tib.toFixed(2)} TiB</strong><span>{c.costo_clp != null ? `≈ ${fmtClpCorto(c.costo_clp)}` : "—"}</span></div>
                  <code className="cst-sql">{c.firma}</code>
                  <span className="sbx-kpi-sub">{c.veces} ejecuciones · {c.usuario.split("@")[0]}</span>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>

      <footer className="cst-fuentes">
        {(Object.entries(data.fuentes) as [string, string | null][]).map(([k, err]) => (
          <span key={k} className={err ? "bad" : "ok"} title={err ?? undefined}>{k}: {err ? "sin respuesta" : "ok"}</span>
        ))}
        <span>Tasa USD→CLP {data.tasa_usd_clp ? data.tasa_usd_clp.toFixed(0) : "sin dato"} (exportación de GCP)</span>
      </footer>
    </div>
  );
}
